import React, { useRef, useState } from "react";
import { HardHat, Info, FileText, Ruler, ShieldCheck, CircleCheck, CircleX, Clock3, ChevronDown, Download, Eye, LoaderCircle, Paperclip } from "lucide-react";
import { fetchDepartmentAttachment, viewDepartmentAttachment } from "./departmentAttachmentPreview";
import { excavationInputValue, formatExcavationDate, getExcavationWorkLocation } from "./excavationViewData";
import "./excavationPermitDetails.css";

const displayValue = value => value === null || value === undefined || value === "" ? "—" : String(value);
const Field = ({ label, value }) => <div className="exc-view-field"><dt>{label}</dt><dd>{displayValue(value)}</dd></div>;
const statusClass = status => status === "Approved" ? "approved" : status === "Rejected" ? "rejected" : "pending";

function DepartmentFindings({ inputs }) {
  const checks = inputs.filter(input => input.field_type === "boolean");
  const details = inputs.filter(input => input.field_type !== "boolean");
  return (
    <section className="exc-view-findings">
      <h4>Findings and precautions</h4>
      {checks.length > 0 && <div className="exc-view-findings-group">
        <h5>Checks</h5>
        <dl className="exc-view-check-grid">{checks.map((input, index) => {
          const value = excavationInputValue(input);
          const answerClass = value === "Yes" ? "yes" : value === "No" ? "no" : "unanswered";
          return <div className="exc-view-input exc-view-check" key={`${input.field_name}:${index}`}>
            <dt>{input.field_name}</dt>
            <dd><span className={`exc-view-answer ${answerClass}`}>
              {value === "Yes" ? <CircleCheck size={16} aria-hidden="true" /> : value === "No" ? <CircleX size={16} aria-hidden="true" /> : null}
              {value}
            </span></dd>
          </div>;
        })}</dl>
      </div>}
      {details.length > 0 && <div className="exc-view-findings-group">
        <h5>Additional details</h5>
        <dl className="exc-view-inputs">{details.map((input, index) => <div className="exc-view-input" key={`${input.field_name}:${index}`}>
          <dt>{input.field_name === "excavation_type" ? "Excavation method" : input.field_name}</dt>
          <dd>{excavationInputValue(input)}</dd>
        </div>)}</dl>
      </div>}
    </section>
  );
}

export default function ExcavationPermitDetails({ work = {}, departments = [], warning, onDownloadAttachment }) {
  const [downloading, setDownloading] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const [viewing, setViewing] = useState(null);
  const fileBusy = useRef(false);
  const sortedDepartments = [...departments].sort((first, second) => (Number(first.approval_order) || 0) - (Number(second.approval_order) || 0));
  const approved = departments.filter(department => department.approval_status === "Approved").length;
  const download = async attachment => {
    if (fileBusy.current) return;
    fileBusy.current = true;
    setDownloading(attachment.id);
    setDownloadError("");
    let url;
    try {
      if (onDownloadAttachment) await onDownloadAttachment(attachment.id, attachment.original_filename);
      else {
        url = URL.createObjectURL(await fetchDepartmentAttachment(attachment));
        const link = document.createElement("a");
        link.href = url;
        link.download = attachment.original_filename || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch {
      setDownloadError("Unable to download the attachment. Please try again.");
    } finally {
      if (url) URL.revokeObjectURL(url);
      setDownloading(null);
      fileBusy.current = false;
    }
  };
  const view = async attachment => {
    if (fileBusy.current) return;
    fileBusy.current = true;
    setViewing(attachment.id);
    setDownloadError("");
    try {
      await viewDepartmentAttachment(attachment);
    } catch (error) {
      setDownloadError(error.message?.includes("pop-ups") ? error.message : "Unable to view the document. Please try again.");
    } finally {
      setViewing(null);
      fileBusy.current = false;
    }
  };
  const generalFields = [
    ["Issued to", typeof work.issued_to === "object" ? work.issued_to?.value : work.issued_to],
    ["Department", work.department_name], ["Receiver", work.receiver_name], ["Contractor", work.contractor],
    ["Work location (Area / Unit)", getExcavationWorkLocation(work)], ["Section", work.section],
    ["Related work permit", work.related_permit_no],
  ];
  const timingFields = [["Valid from", work.valid_from], ["Valid to", work.valid_to], ["Work time from", work.work_time_from], ["Work time to", work.work_time_to]];
  return (
    <div className="excavation-view-details">
      {warning && <div className="exc-view-warning" role="alert">{warning} Showing available information.</div>}
      <section className="exc-view-card">
        <header className="exc-view-heading">
          <div className="exc-view-title"><span className="exc-view-title-icon"><HardHat size={24} /></span><div><h2>Excavation Clearance / Road Closure</h2><p>Work permit details and department reviews</p></div></div>
          <div className="exc-view-badges">
            {work.request_status && <span className="exc-view-status pending">{work.request_status}</span>}
            {(Array.isArray(work.work_type_list) ? work.work_type_list : [work.work_type]).filter(Boolean).map(type => <span className="exc-view-tag" key={type}>{type}</span>)}
          </div>
        </header>
        <div className="exc-view-summary-grid">
          <section className="exc-view-section"><h3><Info size={17} /> General information</h3><dl className="exc-view-field-grid">{generalFields.map(([label, value]) => <Field key={label} label={label} value={value} />)}</dl></section>
          <section className="exc-view-section"><h3><FileText size={17} /> Work details</h3><dl className="exc-view-field-grid">
            <div className="exc-view-wide"><Field label="Work description" value={work.work_description} /></div>
            <Field label="Number of people" value={work.no_of_men} />
            {timingFields.map(([label, value]) => <Field key={label} label={`${label} (IST)`} value={formatExcavationDate(value)} />)}
          </dl></section>
        </div>
        <section className="exc-view-dimensions"><h3><Ruler size={17} /> Excavation dimensions</h3><dl>{[["Length", work.excavation_length], ["Width", work.excavation_width], ["Depth", work.excavation_depth]].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{displayValue(value)} <span>m</span></dd></div>)}</dl></section>
        {work.equipment_details?.length > 0 && <section className="exc-view-equipment"><h3>Equipment</h3><div className="exc-view-badges">{work.equipment_details.map((equipment, index) => <span className="exc-view-tag" key={index}>{typeof equipment === "string" ? equipment : equipment.TagNo || equipment.equipment_no || `Equipment ${index + 1}`}</span>)}</div></section>}
      </section>
      <section className="exc-view-card">
        <header className="exc-view-heading"><div className="exc-view-title"><ShieldCheck size={22} /><div><h2>Department reviews</h2><p>Saved findings, decisions, and supporting documents</p></div></div><span className="exc-view-tag">{approved} of {departments.length} approved</span></header>
        {downloadError && <div className="exc-view-warning" role="alert">{downloadError}</div>}
        {!departments.length ? <p className="exc-view-empty">No department reviews are available for this permit.</p> : <div className="exc-view-departments">{sortedDepartments.map((department, index) => (
          <details className="exc-view-department" key={department.id || department.department_id || index} open={index === 0}>
            <summary><div className="exc-view-department-title"><span className="exc-view-order">{department.approval_order ?? index + 1}</span><span>{department.department_name}</span></div><div className="exc-view-badges">{Number(department.is_parallel) === 1 && <span className="exc-view-tag">Parallel</span>}<span className={`exc-view-status ${statusClass(department.approval_status)}`}>{department.approval_status === "Approved" ? <CircleCheck size={14} /> : <Clock3 size={14} />}{department.approval_status || "Pending"}</span><ChevronDown className="exc-view-chevron" size={18} /></div></summary>
            <div className="exc-view-department-body">
              <dl className="exc-view-reviewer-grid"><Field label="Reviewed by" value={department.approver_name} /><Field label="Designation" value={department.approver_designation} /><Field label="Decision date (IST)" value={formatExcavationDate(department.approved_at)} /></dl>
              {department.inputs?.length > 0 && <DepartmentFindings inputs={department.inputs} />}
              {[["Observations", department.observations], ["Remarks", department.remarks]].filter(([, value]) => value && String(value).trim()).map(([label, value]) => <div className="exc-view-note" key={label}><h4>{label}</h4><p>{value}</p></div>)}
              {department.attachments?.length > 0 && <div className="exc-view-attachments">
                <h4><Paperclip size={15} /> Supporting documents</h4>
                {department.attachments.map(attachment => <div className="exc-view-document" key={attachment.id}>
                  <FileText size={20} aria-hidden="true" />
                  <div className="exc-view-document-info">{attachment.original_filename || "Document"}<small>{String(attachment.file_type || "File").toUpperCase()}{attachment.file_size ? ` · ${(attachment.file_size / 1024 / 1024).toFixed(2)} MB` : ""}</small></div>
                  <div className="exc-view-document-actions">
                    <button type="button" onClick={() => view(attachment)} disabled={Boolean(downloading || viewing) || !attachment.id} aria-label={`View ${attachment.original_filename || "document"} (opens in new tab)`}>
                      {viewing === attachment.id ? <LoaderCircle className="exc-view-loading" size={17} /> : <Eye size={17} />} {viewing === attachment.id ? "Opening…" : "View"}
                    </button>
                    <button type="button" onClick={() => download(attachment)} disabled={Boolean(downloading || viewing) || !attachment.id} aria-label={`Download ${attachment.original_filename || "document"}`}>
                      {downloading === attachment.id ? <LoaderCircle className="exc-view-loading" size={17} /> : <Download size={17} />} Download
                    </button>
                  </div>
                </div>)}
              </div>}
            </div>
          </details>
        ))}</div>}
      </section>
    </div>
  );
}
